import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  MergedRecorder,
  Recorder,
} from "api/interfaces/recorderInterface.interface";
import Status, {
  StatusName,
  StatusValue,
} from "components/blocks/status/Status";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAppSelector } from "redux/hooks";
import * as s from "pages/recorders/RecordersStyled";
import ONLINE_ICON from "assets/icons/online.svg";
import OFFLINE_ICON from "assets/icons/offline.svg";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import {
  useLocation,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import {
  BasePagination,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useQuery } from "react-query";
import * as mediaQuery from "components/MediaQuery";
import { getRecorderList } from "api/recorderAPI";
import { MergedRecorderListResponse } from "api/interfaces/recorderInterface.interface";
import { convertType } from "utils/functions";
import { formatNumber } from "utils/functions";
import { AlertRecorerTypeFilter } from "utils/AlertUtil";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { FaSync } from "react-icons/fa";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import WebAppLink from "components/blocks/link/WebAppLink";
import Type from "components/atoms/text/labels/Type";
import { TableColumnsType } from "antd";
import { changeSortOrderAntd } from "components/atoms/table/AntdTable";
import TableTitle from "components/blocks/table/TableTitle";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import TableButton from "components/atoms/buttons/TableButton";
import { ExpandableConfig } from "antd/es/table/interface";
import RecorderListExpand from "pages/recorders/recorders/list/RecorderListExpand";
import { IoAlertCircle } from "react-icons/io5";
import BigButton from "components/atoms/buttons/BigButton";
import {
  DataRow,
  mergedRecorderToDataRow,
} from "pages/recorders/recorders/list/RecorderListContainer";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { WithTooltip } from "components/blocks/WithTooltip";
import { NeedActionCell } from "pages/recorders/recorders/NeedActionCell";
import RecorderListPresenter from "pages/recorders/recorders/list/RecorderListPresenter";
import { STAGING_TABS, StagingPageOutletContext } from "../StagingPage";
import Tabs from "components/atoms/tabs/Tabs";
import { useFlag } from "@unleash/proxy-client-react";

interface Props {}

const initialQueryInfo = {
  ...baseQueryInfo,
  sortType: "mergedSystemName",
};

export default function StagingRecorderContainer(props: Props) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const location = useLocation();
  const {
    updateQueryInfo,
    onHeaderCell,
    onChangePage,
    setButtons,
    status,
    setCenterChildren,
    setClassName,
    setNoSearch,
    setTitleComponent,
  } = useOutletContext<StagingPageOutletContext>();

  const { menuNavigation, debouncedNavigate } = useMenu({});

  const [recorder, setRecorder] = useState<DataRow[]>([]);
  const [searchParams] = useSearchParams();
  const [pageInfo, setPageInfo] = useState<Partial<BasePagination>>({});
  const [isEditModal, setIsEditModal] = useState<boolean>(false);
  const isStagingRecorderTransferEnable = useFlag(
    "feature_maintenance_staging-transfer"
  );

  const recorderQueryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...initialQueryInfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
    };
  }, [searchParams, pageInfo]);

  const recorderQueryKey = useQueryKey(recorderQueryInfo);

  const [isDeregisterHelp, setIsDeregisterHelp] = useState<boolean>(false);

  const [isLicenseModal, setIsLicenseModal] = useState<boolean>(false);
  const [isRemoveLicenseModal, setIsRemoveLicenseModal] =
    useState<boolean>(false);
  const [selectRow, setSelectRow] = useState<DataRow>();

  const [expandedRowKeys, setExpandedRowKeys] = useState<string | null>(null);
  const isMobile = mediaQuery.useIsMobile();
  const isBigScreen = mediaQuery.useIsBigScreen();
  const [isUpdateFirmwareModal, setIsUpdateFirmwareModal] =
    useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { refetch } = useQuery(
    ["recorder", recorderQueryKey, selectedAccount.accountId],
    () =>
      getRecorderList({
        payload: recorderQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      enabled: true,
      //refetchInterval: 10000,
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (res: MergedRecorderListResponse) => {
        if (res.error !== 0 || !res.result) {
          setRecorder([]);
          setCenterChildren(
            <Status
              status={{
                totalTitle: "Recorders",
                totalValue: 0,
                items: [],
                cloudStatus: false,
              }}
            />
          );
          return;
        }

        setRecorder(
          (res.result.mergedSystemList || []).map(
            (recorder: MergedRecorder): DataRow & { key: string } => {
              return {
                ...mergedRecorderToDataRow(recorder),
                key: recorder.mergedSystemId,
              };
            }
          )
        );
        setCenterChildren(
          <Status
            status={{
              totalTitle: "Recorders",
              totalValue: res.result.summary.totalRecorderCount,
              items: [
                <>
                  <s.Label>
                    <img src={ONLINE_ICON} alt="online icon" width={16} />
                    <StatusName>Online</StatusName>
                  </s.Label>
                  <StatusValue>
                    {formatNumber(res.result.summary.onlineStatusCount)}
                  </StatusValue>
                </>,
                <>
                  <s.Label>
                    <img src={OFFLINE_ICON} alt="offline icon" width={16} />
                    <StatusName>Offline</StatusName>
                  </s.Label>
                  <StatusValue>
                    {formatNumber(
                      res.result.summary.totalRecorderCount -
                        res.result.summary.onlineStatusCount
                    )}
                  </StatusValue>
                  {res.result.summary.autoUpdate && (
                    <RBACWrapper requiredPermissions="manageRecordersAndGroups">
                      <s.Label
                        onClick={() =>
                          menuNavigation({
                            to: `/management/firmware`,
                          })
                        }
                      >
                        <FaSync />
                        <span>Auto Update</span>
                      </s.Label>
                    </RBACWrapper>
                  )}
                </>,
              ],
              cloudStatus: res.result.summary.cloudStatus,
            }}
          />
        );
        setPageInfo(res.page);
      },
      onError: () => {
        setRecorder([]);
        setCenterChildren(
          <Status
            status={{
              totalTitle: "Recorders",
              totalValue: 0,
              items: [],
              cloudStatus: false,
            }}
          />
        );
      },
      onSettled: () => {
        setPending(false);
      },
    }
  );

  const onChangeReportType = useCallback(
    (value: string | undefined) => {
      if (value === undefined) return;
      updateQueryInfo({
        type: value === "all" ? null : value,
        pageNum: 0,
      });
    },
    [updateQueryInfo]
  );

  const onClickUpdateFirmware = (row: DataRow) => {
    setSelectRow(row);
    setIsUpdateFirmwareModal(true);
  };

  const columns: TableColumnsType<DataRow> = [
    {
      key: "type",
      title: (
        <TableColumnFilterTitle
          title="Type"
          filters={AlertRecorerTypeFilter}
          onClick={onChangeReportType}
        />
      ),
      width: isMobile ? 90 : 110,
      render: (value, row) => (
        <Type
          type={convertType(row)}
          licenseType={row.licenseType}
          isCloudCam={row.isCloudCam}
        />
      ),
    },
    {
      title: "Title",
      sorter: true,
      key: "mergedSystemName",
      defaultSortOrder: "ascend",
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "mergedSystemName"),
      onHeaderCell: onHeaderCell,
      render: (value, row) =>
        row.recorderCount !== undefined && row.recorderCount > 0 ? (
          <TableTitle row={row} cloudStatus={status.cloudStatus} />
        ) : (
          <Fragment />
        ),
      ellipsis: true,
    },
    {
      key: "access",
      title: "Access",
      render: (value, row) => (
        <s.WebAppRow onClick={(e) => e.stopPropagation()}>
          <WebAppLink
            {...row}
            displayType={convertType(row)}
            accountId={selectedAccount.accountId}
            recorderId={row.recordId || ""}
            cloudStatus={status.cloudStatus}
          />
        </s.WebAppRow>
      ),
      hidden: selectedAccount.isHidden || !selectedAccount.isEnabled,
      width: isMobile ? 120 : "10%",
    },
    {
      title: "Version",
      render: (value, row) =>
        (!status.autoUpdate as unknown as boolean) &&
        row.isUpdate &&
        row.enableEdit &&
        row.onlineStatusCount === row.recorderCount ? (
          <s.Row onClick={() => onClickUpdateFirmware(row)}>
            {row.version}{" "}
            <WithTooltip tooltip="Available firmware update">
              <IoAlertCircle className="firmware-icon" size={15} />
            </WithTooltip>
          </s.Row>
        ) : (
          row.version
        ),
      responsive: ["xl"],
      width: isBigScreen ? "15%" : "10%",
    },
    {
      title: "Registered Date",
      render: (value, row) =>
        getAccountTimeFormatDate(
          row.createdAt || "",
          selectedAccount,
          true,
          false
        ),
      responsive: ["lg"],
      width: isBigScreen ? "15%" : 150,
      sorter: true,
      key: "createdAt",
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "createdAt"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Cameras",
      render: (value, row) =>
        row.cameras > 0 ? (
          <s.RowLink
            onClick={() => {
              if (!selectedAccount.isEnabled) return;
              menuNavigation({
                to: `/maintenance/staging/cameras/${row.mergedSystemId}${
                  row.recorderId ? `/${row.recorderId}` : ""
                }`,
              });
            }}
            disabled={!selectedAccount.isEnabled}
          >
            {row.cameras}
          </s.RowLink>
        ) : (
          row.cameras
        ),
      align: "center",
      width: isMobile ? 60 : 100,
    },
    {
      title: "",
      width: 150,
      render: (value, row) =>
        selectedAccount.isEnabled && (
          <NeedActionCell recorder={row as DataRow & MergedRecorder} />
        ),
      responsive: ["xl"],
    },
    {
      title: "",
      width: isMobile ? 40 : 80,
      render: (row) => (
        <TableButton
          label="Edit"
          onClick={(e) => {
            e.stopPropagation();
            onClickEdit(row);
          }}
        />
      ),
      key: "edit",
      align: "right",
    },
  ];

  const onModal = (type: string, row?: DataRow) => {
    if (type === "license") {
      setIsLicenseModal(!isLicenseModal);
    }
    if (type === "remove") {
      setIsRemoveLicenseModal(!isRemoveLicenseModal);
    }
    if (type === "edit") {
      setIsEditModal(!isEditModal);
    }
  };

  const onClickEdit = (row: DataRow) => {
    debouncedNavigate({
      to: `/maintenance/staging/edit`,
      queryString: {
        isMergedSystem: row.recorderCount > 1,
        systemId: row.recordId,
      },
    });
  };

  const [pending, setPending] = useState<boolean>(true);

  const expandableProps = useMemo<ExpandableConfig<DataRow>>(() => {
    return {
      expandRowByClick: false,
      expandedRowKeys: expandedRowKeys ? [expandedRowKeys] : [],
      onExpand: (expanded: boolean, record: DataRow) => {
        if (expanded) {
          setExpandedRowKeys(record.mergedSystemId);
        } else {
          setExpandedRowKeys(null);
        }
      },
      expandedRowRender: (record: DataRow) => (
        <RecorderListExpand
          mergedSystemId={record.mergedSystemId}
          columns={columns as TableColumnsType<Recorder>}
          queryInfo={recorderQueryInfo}
        />
      ),
      rowExpandable: (record: DataRow) =>
        convertType(record) === "SPECTRUM" && record.recorderCount > 1,
    };
  }, [expandedRowKeys, onClickEdit]);

  useStompDebouncedRefetch({
    selector: (state) =>
      ({
        recorderListUpdated: state.stomp.recorderListUpdated,
        subscriptionActivatedUpdated:
          state.subscriptionStomp.subscriptionActivatedUpdated,
      } as Record<string, unknown>),
    stompKey: [
      "recorderListUpdated",
      "subscriptionActivatedUpdated",
    ] as StompDateSignalKey[],
    refetch: refetch,
  });

  useEffect(() => {
    setButtons(
      isStagingRecorderTransferEnable ? (
        <RBACWrapper requiredPermissions="manageAccount">
          <BigButton
            label="Assign to EU"
            onClickBtn={() =>
              menuNavigation({
                to: "/maintenance/staging/assign",
              })
            }
          />
        </RBACWrapper>
      ) : null
    );
    setTitleComponent(
      <Tabs
        tabs={[
          { label: "Recorder", value: STAGING_TABS.RECORDER },
          { label: "History", value: STAGING_TABS.HISTORY },
        ]}
        onChangeTab={(value) => {
          menuNavigation({
            to: value,
          });
        }}
        currentTab={location.pathname}
      />
    );
    setClassName("");
    setNoSearch(false);
  }, [setButtons, location.pathname, isStagingRecorderTransferEnable]);
  return (
    <RecorderListPresenter
      autoUpdate={false}
      onModal={onModal}
      selectRow={selectRow as DataRow}
      data={recorder}
      columns={columns as TableColumnsType<DataRow>}
      paginationInfo={recorderQueryInfo}
      onChangePage={onChangePage}
      initialPending={pending}
      isDeregisterHelp={isDeregisterHelp}
      closeDeregisterHelp={() => {
        setIsDeregisterHelp(false);
      }}
      expandableProps={expandableProps}
      isUpdateFirmwareModal={isUpdateFirmwareModal}
      onCancelUpdateFirmware={() => setIsUpdateFirmwareModal(false)}
      isEditModal={isEditModal}
      openDeregisterHelp={() => setIsDeregisterHelp(true)}
    />
  );
}
